/**
 * EmailThreadPanel - Slideout for viewing a full email thread.
 *
 * Mounted by `SlideoutRenderer` when the top entry of the slideout stack
 * is an `email_thread` kind. Composite entry id format: `${proposalId}-${actionId}`.
 *
 * Built on `SlideoutMenu` from `@/lib/base/overlays`, so animation,
 * backdrop, focus management, dismiss-on-Escape, dismiss-on-outside-click,
 * and z-index stacking all come from the design-system wrapper. Entity
 * badges rendered inside the thread can `pushSlideout({ kind: 'entity', id })`
 * to layer on top — closing them reveals this panel again.
 *
 * Content regions (header + message list) live in `EmailThreadSurface` and
 * are shared with `InlineEmailThreadPanel`, which hosts the same surface as
 * a real child of the AI actions page's SplitView instead of an overlay.
 */

import { useCallback, useMemo } from 'react';

import { useOverlayOrder } from '@/contexts/OverlayOrderContext';
import { useSlideoutWidth } from '@/contexts/SlideoutWidthContext';
import { StackingLevelProvider } from '@/contexts/StackingContext';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import { type SlideoutEntry } from '@/lib/crm/contexts/SlideoutStackContext';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';

import {
  EmailThreadBodyContent,
  EmailThreadHeaderContent,
} from './EmailThreadSurface';

export interface EmailThreadPanelProps {
  /** Stack entry this slideout renders. Must be `kind: 'email_thread'`. */
  entry: SlideoutEntry;
}

/**
 * Slideout for viewing a full email thread.
 */
export default function EmailThreadPanel({ entry }: EmailThreadPanelProps) {
  const { clearToProposalBase } = useSlideoutStack();
  // Share this slideout's resizable width with the rest of the app via
  // SlideoutWidthContext (only the topmost slideout renders, so the context
  // always reflects the visible slideout). The floating composer reads this
  // to compute its right offset — without it the composer would offset by a
  // stale/default width and overlap this panel once it's resized.
  const { width: slideoutWidth, setWidth: setSlideoutWidth } =
    useSlideoutWidth();

  // Order-based stacking level, exactly like CrmObjectSlideout and
  // ProposalSidebar. Without this the SlideoutMenu computes its z-index from
  // the mount point's StackingContext (level 0 → z 400/410), which puts the
  // whole panel UNDER the global SlideoutBackdrop (z-500 in Root.tsx) — the
  // dim overlay then paints over the panel and swallows its clicks. The
  // shared open-order counter also keeps this panel above any modal that was
  // already open when the thread was pushed (e.g. the email preview modal's
  // "In reply to:" row).
  const overlayOrder = useOverlayOrder(entry.id);

  // Dismissing the panel (X, click-outside, Esc) reveals the proposal it was
  // opened over, or closes everything when no proposal is on the stack (e.g.
  // on the full-page actions route). One-level back-navigation is the entity
  // slideout header's back button; this panel has no back affordance.
  const handleClose = useCallback(() => {
    clearToProposalBase();
  }, [clearToProposalBase]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  const header = useMemo(
    () => <EmailThreadHeaderContent entry={entry} />,
    [entry]
  );
  const body = useMemo(() => <EmailThreadBodyContent entry={entry} />, [entry]);

  return (
    <StackingLevelProvider level={overlayOrder}>
      <SlideoutMenu
        isOpen={true}
        onOpenChange={handleOpenChange}
        isDismissable
        resizable
        initialWidth={slideoutWidth}
        onWidthChange={setSlideoutWidth}
        showBackdrop={false}
        header={header}
        dialogAriaLabel='Email thread'
      >
        {body}
      </SlideoutMenu>
    </StackingLevelProvider>
  );
}
